import { iconFileUp } from "@kensho/icons";
import { useLogger } from "@kensho/lumberjack";
import { Button, Tooltip, useToaster } from "@kensho/neo";
import { use, useCallback } from "react";
import { useDropzone } from "react-dropzone";

import { APITranscript, TranscriptSelection } from "../../../types/types";
import { camelCaseDeep } from "../../../utils/camel-case-deep";
import {
  produceEditorAction,
  TranscriptEditAction,
} from "../../../utils/transcript-revision-utils";
import { TranscriptPermissionsContext } from "../transcript-permissions-provider";
import { useDispatchEditorAction } from "../transcript/dispatch-editor-action-provider";

interface Props {
  onEditOperationError: (error: Error, action: TranscriptEditAction) => void;
  transcriptSelection: TranscriptSelection | null;
  transcript: APITranscript;
  version: number;
  transcriptId: string;
}

export function ImportTranscriptButton(props: Props) {
  const {
    onEditOperationError,
    transcript,
    version,
    transcriptId,
    transcriptSelection,
  } = props;
  const { transcriptPermissions } = use(TranscriptPermissionsContext);
  const { dispatchEditorAction } = useDispatchEditorAction();
  const toaster = useToaster();
  const log = useLogger();
  const handleImport = useCallback(
    async (acceptedFiles: File[]) => {
      let nextTranscript: APITranscript;
      try {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        const readJSONPromise = new Promise((resolve) => {
          reader.onload = (event) => {
            if (!event.target) throw new Error("Empty result");
            const data = camelCaseDeep(
              JSON.parse(event.target.result as string),
            );
            resolve(data);
          };
        });
        reader.readAsText(file);
        nextTranscript = (await readJSONPromise) as APITranscript;
      } catch (error) {
        log.error(error as Error);
        toaster.show({
          label: `Failed to import ${acceptedFiles[0].name} `,
          intent: "danger",
        });
        return;
      }

      log.info("Import transcript from JSON", { transcriptId });

      const editorAction = produceEditorAction({
        action: { type: "replace-transcript", data: nextTranscript },
        transcript,
        transcriptSelection,
        onError: onEditOperationError,
        log,
      });
      if (!editorAction) return;

      dispatchEditorAction(editorAction, version);
    },
    [
      transcript,
      version,
      transcriptId,
      transcriptSelection,
      onEditOperationError,
      log,
      dispatchEditorAction,
      toaster,
    ],
  );
  const { getInputProps, getRootProps, open } = useDropzone({
    onDropAccepted: handleImport,
    accept: {
      "application/json": [".json"],
    },
    multiple: false,
    noDrag: true,
    useFsAccessApi: false,
    disabled: !transcriptPermissions.edit,
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <Tooltip content="Import from JSON" position="bottom">
        <Button
          aria-label="Import from JSON"
          icon={iconFileUp}
          disabled={!transcriptPermissions.edit}
          minimal
          intent="primary"
          onClick={() => open()}
        />
      </Tooltip>
    </div>
  );
}
